import { selectorByIdBuilder } from './selectors';
import { uniqueSymmetricDifference } from './arrayService';
import { sortByField } from './sorting';
import { generateGuid } from 'services/Authentication';
import { has, get } from 'lodash';

export const mapInwardDependenciesToSelectionData = (availableSelections, currentSelection, dependencyKey) => {
    return mapDependenciesToSelectionData(availableSelections, currentSelection, dependencyKey, true);
};

export const mapOutwardDependenciesToSelectionData = (availableSelections, currentSelection, dependencyKey) => {
    return mapDependenciesToSelectionData(availableSelections, currentSelection, dependencyKey, false);
};

/**
 * turn currentSelection dependencies into a selectable list for use in the currentSelection select dependency screens
 * NOTE - only works for data in this structure
 * "dependencies": [
    {
        "outward": {
            "id": "9648cb65923bb7ba0845014820190617"
        },
        "id": "2578cb65923bb7ba0845014820176298"
    },
    {
        "id": "2578cb65923bb7ba0845014820176435",
        "inward": {
            "id": "3875cb65923bb7ba0845014820190617"
        }
    }
],
 * @param {*} availableSelections - the full list to select from
 * @param {*} currentSelection - the current selection (filter from the available list)
 * @param {*} dependencyKey - the object entry that contains the dependency data - defaults to 'dependencies'
 * @param {*} inward 
 */
export const mapDependenciesToSelectionData = (availableSelections, currentSelection, dependencyKey = 'dependencies', inward) => {
    let selectionList;
    let result = new Array();
    if (has(currentSelection, dependencyKey)) {
        const dependencies = get(currentSelection, dependencyKey);
        if (Array.isArray(dependencies) && dependencies?.length > 0) {
            const linkedList = new Array();
            dependencies?.map((dependency) => {
                let linkedId;
                if (inward) {
                    if (has(dependency, 'inward')) {
                        linkedId = dependency?.inward?.id;
                    }
                } else {
                    if (has(dependency, 'outward')) {
                        linkedId = dependency?.outward?.id;
                    }
                }
                if (linkedId) {
                    const foundDependency = availableSelections?.find((element) => element?.id === linkedId);
                    if (foundDependency) {
                        linkedList.push(foundDependency);
                    }
                }
            });
            selectionList = selectorByIdBuilder(linkedList, 'id', 'name', true);
        }
        // need to remove the current currentSelection from the availableSelections list
        const selectable = availableSelections.filter((entry) => entry?.id !== currentSelection?.id);
        const nonLinkedSelections = uniqueSymmetricDifference(selectable, selectionList);
        // sort the selections for display
        const nonLinkedSelectionsSorted = sortByField(nonLinkedSelections, 'name');
        // now convert for currentSelection
        const mappedNoLinkedSelections = selectorByIdBuilder(nonLinkedSelectionsSorted, 'id', 'name', false);
        if (Array.isArray(mappedNoLinkedSelections) && Array.isArray(selectionList)) {
            result = selectionList.concat(mappedNoLinkedSelections);
        } else if (Array.isArray(mappedNoLinkedSelections)) {
            result = mappedNoLinkedSelections;
        } else if (Array.isArray(selectionList)) {
            result = selectionList;
        }
    }
    return result;
};

/**
 * take the selections and work out which items need to be updated
 * @param {Array} allItems - the full list of items that could be selected from
 * @param {*} selectedItem - the actual item being changed (adding or removed dependencies)
 * @param {*} dependencyList 
 * @param {*} inward 
 */
export const mapSelectionsToDependencyData = (allItems, selectedItem, dependencyList, inward) => {
    let updatedItems = new Array();
    let updated = false;
    const selectedDirection = inward ? 'inward' : 'outward';
    const relatedDependencyDirection = inward ? 'outward' : 'inward';
    if (Array.isArray(dependencyList)) {
        dependencyList.forEach((selection) => {
            if (selection?.selected !== selection?.originalValue) {
                // have a change so process the update
                const selectedItemDependency = selectedItem?.dependencies
                    .filter((dependency) => dependency[selectedDirection])
                    .find(element => element[selectedDirection]?.id === selection?.id);
                const foundSelectedDependency = selectedItemDependency !== undefined;
                const relatedItem = allItems.find(element => element?.id === selection?.id);
                const relatedItemDependency = relatedItem?.dependencies
                    .filter((dependency) => dependency[relatedDependencyDirection])
                    .find(element => element[relatedDependencyDirection].id === selectedItem?.id);
                if (selection?.selected) {
                    // add the incoming dependency to the selected feature
                    if (!foundSelectedDependency) {
                        // add to the selected feature dependencies
                        let newDependency = {
                            id: generateGuid(),
                        };
                        newDependency[selectedDirection] = { id: selection?.id };
                        selectedItem?.dependencies.push(newDependency);
                        updated = true;
                    }
                    if (relatedItem) {
                        const foundRelatedItemDependency = relatedItemDependency !== undefined;
                        // should not be found
                        if (!foundRelatedItemDependency) {
                            // add to the selected feature dependencies
                            let newDependency = {
                                id: generateGuid(),
                            };
                            newDependency[relatedDependencyDirection] = { id: selectedItem?.id };
                            relatedItem?.dependencies.push(newDependency);
                            const currentIndex = updatedItems.findIndex((element) => element?.id === relatedItem?.id);
                            if (currentIndex === -1) {
                                updatedItems.push(relatedItem);
                            } else {
                                updatedItems.splice(currentIndex, 1).push(relatedItem);
                            }
                            updated = true;
                        }
                    }
                } else {
                    if (foundSelectedDependency) {
                        // need to remove dependencies from selected and item in the list
                        const filteredSelectedDependencies = selectedItem?.dependencies?.filter((dependency) => dependency?.id !== selectedItemDependency?.id);
                        selectedItem = { ...selectedItem, dependencies: filteredSelectedDependencies };
                        updated = true;
                    }
                    if (relatedItem) {
                        const foundRelatedItemDependency = relatedItemDependency !== undefined;
                        // should be found
                        if (foundRelatedItemDependency) {
                            const filteredRelatedItemDependencies = relatedItem?.dependencies?.filter((dependency) => dependency?.id !== relatedItemDependency?.id);
                            const updatedRelatedItem = { ...relatedItem, dependencies: filteredRelatedItemDependencies };
                            const currentIndex = updatedItems.findIndex((element) => element?.id === relatedItem?.id);
                            if (currentIndex === -1) {
                                updatedItems.push(updatedRelatedItem);
                            } else {
                                updatedItems.splice(currentIndex, 1).push(updatedRelatedItem);
                            }
                            updated = true;
                        }
                    }
                }
            }
        });
        if (updated) {
            // send updatedFeatures for update via the api
            updatedItems = updatedItems.concat(selectedItem);
        }
    }
    return updatedItems;
};





// WEBPACK FOOTER //
// ./services/dependencyService.js